import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { GridOn } from '@mui/icons-material';

const Unidades = () => {
    // Filtros y paginación
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);

    // Exportar a Excel
    const handleExportExcel = async () => {
      const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
      const filtered = unidades.filter(u =>
        u.nombre.toLowerCase().includes(searchValue.toLowerCase()) ||
        u.abreviatura.toLowerCase().includes(searchValue.toLowerCase()) ||
        (u.codigo_sunat || '').toLowerCase().includes(searchValue.toLowerCase())
      );
      const rows = (rowsPerPage === -1 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
        .map(u => ({
          id: u.id,
          nombre: u.nombre,
          abreviatura: u.abreviatura,
          codigo_sunat: u.codigo_sunat || ''
        }));
      await exportGenericWorkbook({
        titulo: 'REPORTE - UNIDADES DE MEDIDA',
        sheetName: 'Unidades',
        tableName: 'tblUnidades',
        fileName: 'Unidades',
        columns: [
          { key: 'id', label: 'ID', width: 8 },
          { key: 'nombre', label: 'Nombre', width: 24 },
          { key: 'abreviatura', label: 'Abreviatura', width: 16 },
          { key: 'codigo_sunat', label: 'Código SUNAT', width: 18 }
        ],
        rows,
        searchText: searchValue,
        usuario: ''
      });
    };
  const [unidades, setUnidades] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [nombre, setNombre] = useState('');
  const [abreviatura, setAbreviatura] = useState('');
  const [codigoSunat, setCodigoSunat] = useState('');

  const fetchUnidades = async () => {
    const res = await axios.get('/api/unidades');
    setUnidades(res.data);
  };

  useEffect(() => {
    fetchUnidades();
  }, []);

  const handleOpen = (unidad = null) => {
    if (unidad) {
      setEditId(unidad.id);
      setNombre(unidad.nombre);
      setAbreviatura(unidad.abreviatura || '');
      setCodigoSunat(unidad.codigo_sunat || '');
    } else {
      setEditId(null);
      setNombre('');
      setAbreviatura('');
      setCodigoSunat('');
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setNombre('');
    setAbreviatura('');
    setCodigoSunat('');
  };

  const handleSave = async () => {
    if (!nombre) return;
    const data = { nombre, abreviatura, codigo_sunat: codigoSunat };
    try {
      if (editId) {
        await axios.put(`/api/unidades/${editId}`, data);
      } else {
        await axios.post('/api/unidades', data);
      }
      fetchUnidades();
      handleClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/unidades/${id}`);
      fetchUnidades();
    } catch { alert('Error al eliminar'); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Unidades de Medida</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar..."
            value={searchValue}
            onChange={e => { setSearchValue(e.target.value); setPage(0); }}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button color="success" variant="contained" sx={{ minWidth: 120 }} onClick={handleExportExcel} startIcon={<GridOn />}>
            Exportar Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>Nueva Unidad</Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ pr: 8 }}>ID</TableCell>
                <TableCell align="center">Nombre</TableCell>
                <TableCell align="center">Abreviatura</TableCell>
                <TableCell align="center">Código SUNAT</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unidades
                .filter(u => {
                  const search = searchValue.toLowerCase();
                  return (
                    u.nombre.toLowerCase().includes(search) ||
                    u.abreviatura.toLowerCase().includes(search) ||
                    (u.codigo_sunat || '').toLowerCase().includes(search) ||
                    u.id.toString().includes(search)
                  );
                })
                .slice(rowsPerPage === -1 ? 0 : page * rowsPerPage, rowsPerPage === -1 ? undefined : (page + 1) * rowsPerPage)
                .map((unidad) => (
                  <TableRow key={unidad.id}>
                    <TableCell align="center" sx={{ pr: 8 }}>{unidad.id}</TableCell>
                    <TableCell align="center">{unidad.nombre}</TableCell>
                    <TableCell align="center">{unidad.abreviatura}</TableCell>
                    <TableCell align="center">{unidad.codigo_sunat}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleOpen(unidad)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(unidad.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginacionControl
          page={page}
          rowsPerPage={rowsPerPage}
          count={unidades.filter(u => {
            const search = searchValue.toLowerCase();
            return (
              u.nombre.toLowerCase().includes(search) ||
              u.abreviatura.toLowerCase().includes(search) ||
              (u.codigo_sunat || '').toLowerCase().includes(search) ||
              u.id.toString().includes(search)
            );
          }).length}
          onPageChange={setPage}
        />
        <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold">
              {editId ? 'Editar Unidad' : 'Nueva Unidad'}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Abreviatura"
              value={abreviatura}
              onChange={e => setAbreviatura(e.target.value)}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Código SUNAT"
              value={codigoSunat}
              onChange={e => setCodigoSunat(e.target.value)}
              fullWidth
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">Cancelar</Button>
            <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>Guardar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Unidades;
